import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { Lightbox } from '../Lightbox/Lightbox';
import { PROJECT_NAME, PROJECT_PAGES } from '../../data/woodworking';
import { PREMIUM } from '../../data/premium';
import { useSlideTrack } from '../../hooks/useSlideTrack';
import { preloadImages } from '../../utils/preload';
const arrowClass = 'flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-150 ease-out hover:border-brass-light hover:text-brass-light disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-brass-light/40';

/** Real pages of the flagship project (3-tier square plant stand, Volume 03). */
export function ProjectPreviewSection() {
  const total = PROJECT_PAGES.length;
  const {
    trackRef,
    active,
    goTo
  } = useSlideTrack(total);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const current = PROJECT_PAGES[active];
  const sectionRef = useRef<HTMLElement>(null);
  const [warm, setWarm] = useState(false);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const start = () => {
      setWarm(true);
      preloadImages(PROJECT_PAGES.map(page => page.src));
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        start();
        observer.disconnect();
      }
    }, {
      rootMargin: '800px 0px'
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  const navigateLightbox = (index: number) => {
    setLightboxIndex(index);
    goTo(index, true);
  };
  return <section ref={sectionRef} id="project-preview" aria-labelledby="preview-title" className="xmas-glow bg-charcoal py-12 text-white sm:py-20" data-id="element-309" data-mp="23s6ucrub8gmx">
      <div className="mx-auto grid w-full max-w-6xl px-5 sm:px-8 lg:grid-cols-12 lg:gap-x-14" data-id="element-310" data-mp="1yqra31zyie5l">
        <header className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-311" data-mp="2hvckv61g116o">
          <p className="label-tech flex items-center gap-3 text-brass-light" data-id="element-312" data-mp="2mpqo1umckclw">
            <span className="h-[2px] w-6 shrink-0 bg-arc" aria-hidden data-id="element-313" data-mp="28xu082rpoovb" />
            Real pages · {PREMIUM.editionShort}
          </p>
          <h2 id="preview-title" className="mt-3 text-balance font-brand text-[1.85rem] font-bold leading-[1.12] text-white sm:text-[2.5rem]" data-id="element-314" data-mp="2dtrdu1blbh4e">
            Four legs. Three shelves. <em className="text-brass-light" data-id="element-315" data-mp="35lqhzayvxq84">Sells every spring.</em>
          </h2>
          <p className="mt-3 text-[0.95rem] font-semibold text-parchment" data-id="element-316" data-mp="2ro1ya6gk0p4n">{PROJECT_NAME}</p>
          <p className="mt-2 max-w-md text-[0.95rem] leading-snug text-muted" data-id="element-317" data-mp="2wlk77wukn4cn">Every one of the 30 projects is documented like this.</p>
        </header>

        <div className="mt-6 lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-318" data-mp="1ga6hyudwyabu">
          <div className="relative mx-auto w-full max-w-[480px]" data-id="element-319" data-mp="1l53ljlsfbrhc">
            <ul ref={trackRef} tabIndex={0} aria-label={`${PROJECT_NAME} pages, ${total} total.`} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto rounded-[8px] bg-white shadow-[0_40px_70px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/10 focus:outline-none" data-id="element-320" data-mp="1dmciji0anndx">
              {PROJECT_PAGES.map((page, index) => <li key={page.src} className="w-full shrink-0 snap-center" data-id="element-321" data-mp="1hn662a9tr06a" data-mpi={index}>
                  <button type="button" onClick={() => setLightboxIndex(index)} aria-label={`Enlarge page ${index + 1}: ${page.caption}`} className="block aspect-[4/5] w-full cursor-zoom-in" data-id="element-322" data-mp="u0hh54mee7g6">
                    <img src={page.src} alt={page.alt} width={page.width} height={page.height} loading={index === 0 || warm ? 'eager' : 'lazy'} decoding="async" draggable={false} className="h-full w-full object-contain" data-id="element-323" data-mp="13q9ichp05ytm" />
                  </button>
                </li>)}
            </ul>
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-charcoal/90 px-3 py-1.5 text-[0.8rem] font-semibold text-white ring-1 ring-white/15" data-id="element-324" data-mp="69c40x7udt9k">
              <ZoomInIcon className="h-4 w-4 text-brass-light" aria-hidden data-id="element-325" data-mp="owfat5mvtoiu" />
              Tap to enlarge
            </span>
          </div>
        </div>

        <div className="mx-auto mt-4 w-full max-w-[480px] lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:mx-0 lg:mt-8 lg:max-w-none lg:self-start" data-id="element-326" data-mp="ylgup6wjsq23">
          <div className="flex items-center justify-between gap-4" data-id="element-327" data-mp="12msntmemrtf1">
            <div className="min-w-0" aria-live="polite" data-id="element-328" data-mp="17llwn1iz2w8v">
              <p className="label-tech text-brass-light" data-id="element-329" data-mp="17o830xu4n5pi">Page {active + 1} of {total}</p>
              <p className="mt-1 font-brand text-[1.05rem] font-bold leading-snug text-white" data-id="element-330" data-mp="1cjf746rb6cey">{current.caption}</p>
            </div>
            <div className="flex shrink-0 gap-2" data-id="element-331" data-mp="3edeeih1b89md">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={arrowClass} data-id="element-332" data-mp="3eephwfquffcy">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-333" data-mp="20gl8z4kbgyfv" />
              </button>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === total - 1} aria-label="Next page" className={arrowClass} data-id="element-334" data-mp="1vq4pp3tteill">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-335" data-mp="1rpb1sagdh3sp" />
              </button>
            </div>
          </div>
          <ol className="mt-3 grid grid-cols-4 gap-2" aria-label="Pages" data-id="element-336" data-mp="1mnc0d6470gye">
            {PROJECT_PAGES.map((page, index) => <li key={page.src} data-id="element-337" data-mp="2fedjfib0awn7" data-mpi={index}>
                <button type="button" onClick={() => goTo(index)} aria-label={`Show page ${index + 1}: ${page.caption}`} className={`block aspect-[4/5] w-full overflow-hidden rounded-[4px] bg-white ${index === active ? 'opacity-100 ring-2 ring-brass-light' : 'opacity-50 ring-1 ring-white/20 hover:opacity-90'}`} data-id="element-338" data-mp="2ff5gyyo5z4g4">
                  <img src={page.src} alt="" width={page.width} height={page.height} loading="lazy" decoding="async" className="h-full w-full object-contain" data-id="element-339" data-mp="z1fbbnhec84d" />
                </button>
              </li>)}
          </ol>
        </div>
      </div>

      <Lightbox items={PROJECT_PAGES} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={navigateLightbox} label={PROJECT_NAME} counterNoun="Page" data-id="element-340" data-mp="10grijjusxedg" />
    </section>;
}